import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query/react";
import { dynamicMiddleware, rootReducer } from "@momentum/redux-store";
import { createAction, type WithSlice } from "@reduxjs/toolkit";
import {
  authConfigSchema,
  teamMetadataMapSchema,
  userInfoSchema,
  type AuthConfig,
  type TeamMetadataMap,
  type UserInfo,
} from "@momentum/betslurp-schemas";

type BetslurpBaseQueryExtraOptions = Record<string, unknown>;

type BetslurpBaseQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  BetslurpBaseQueryExtraOptions,
  FetchBaseQueryMeta
>;

export interface BetslurpApiConfig {
  baseUrl: string;
  prepareHeaders?: (headers: Headers) => Promise<Headers>;
}

export const betslurpApiConfig: BetslurpApiConfig = {
  baseUrl: "/api",
};

const baseQuery: BetslurpBaseQuery = async (args, api, extraOptions) => {
  const fetchBaseQueryFn = fetchBaseQuery({
    baseUrl: betslurpApiConfig.baseUrl,
    prepareHeaders: betslurpApiConfig.prepareHeaders,
  });
  const result = await fetchBaseQueryFn(args, api, extraOptions);
  return result;
};

export const hydrateApi = createAction<any>("betslurpApi/hydrate");

export const betslurpApi = createApi({
  reducerPath: "betslurpApi",
  baseQuery,
  endpoints: (builder) => ({
    authConfig: builder.query<AuthConfig, void>({
      query: () => "/auth/config",
      responseSchema: authConfigSchema,
    }),
    teamMetadata: builder.query<TeamMetadataMap, void>({
      query: () => "/sportsdata/teamMetadata",
      responseSchema: teamMetadataMapSchema,
      transformResponse: (response: { content: TeamMetadataMap }) =>
        response.content,
    }),
    userInfo: builder.query<UserInfo | null, void>({
      queryFn: async (_args, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery("/me");
        return {
          data: result.data ? (result.data as UserInfo) : null,
        };
      },
      responseSchema: userInfoSchema,
    }),
  }),
  extractRehydrationInfo(action, { reducerPath }) {
    if (hydrateApi.match(action)) {
      return action.payload;
    }
    return undefined;
  },
});

rootReducer.inject(betslurpApi);

declare module "@momentum/redux-store" {
  export interface LazyLoadedSlices extends WithSlice<typeof betslurpApi> {}
}

dynamicMiddleware.addMiddleware(betslurpApi.middleware);
